/**
 * CulsightLoader
 * ----------------
 * One branded loader component for the whole app. Use it everywhere a
 * loading spinner is needed — just change the `variant` prop.
 *
 * VARIANTS
 *  - "page"    : full-page — a centered BOX/CARD with spinner + label inside,
 *                the whole thing centered on the page (first load of a page)
 *  - "overlay" : absolute-positioned overlay — a centered BOX/CARD with
 *                spinner inside, floating over existing content. Wrap the
 *                parent in `className="relative"` and drop this inside it
 *  - "inline"  : small spinner for buttons / inline text (no box, no wrapper)
 *
 * USAGE
 *   // Full page (first load)
 *   if (initialLoading) return <CulsightLoader variant="page" label="Loading campaigns..." />;
 *
 *   // Overlay (refetch on filter/tab change)
 *   <div className="relative">
 *     {refreshing && <CulsightLoader variant="overlay" />}
 *     <table>...</table>
 *   </div>
 *
 *   // Inline (inside a button)
 *   <Button disabled={saving}>
 *     {saving && <CulsightLoader variant="inline" />} Save
 *   </Button>
 */
const SIZE_MAP = {
  page: "h-10 w-10 border-4",
  overlay: "h-8 w-8 border-4",
  inline: "h-4 w-4 border-2",
};

const CulsightLoader = ({
  variant = "page",
  color = "#8c3add",
  label = "Culsight",
  className = "",
}) => {
  const spinnerSize = SIZE_MAP[variant] || SIZE_MAP.page;

  const spinner = (
    <div
      className={`animate-spin rounded-full border-t-transparent ${spinnerSize}`}
      style={{ borderColor: color, borderTopColor: "transparent" }}
    />
  );

  // Inline: just the spinner, no box, no wrapper
  if (variant === "inline") {
    return <span className={`inline-flex mr-2 ${className}`}>{spinner}</span>;
  }

  // Shared "box" — a centered card with spinner + label inside it,
  // used by both "page" and "overlay" variants.
  const box = (
    <div className="flex flex-col items-center justify-center gap-3 bg-white shadow-md border border-gray-100 rounded-2xl px-8 py-6 min-w-[180px]">
      {spinner}
      <p className="text-sm font-medium text-gray-500">{label}</p>
    </div>
  );

  if (variant === "overlay") {
    // Covers the whole parent (so the dimmed background spans the full
    // list/table), but the spinner box itself is `sticky` near the top —
    // so it always stays visible in the viewport even if the parent is
    // very tall (a long table/list), instead of centering in the middle
    // of content that may be scrolled out of view.
    return (
      <div
        className={`absolute inset-0 bg-white/60 z-10 rounded-xl ${className}`}
      >
        <div className="sticky top-10 flex items-center justify-center py-4">
          {box}
        </div>
      </div>
    );
  }

  // variant === "page"
  return (
    <div className={`flex items-center justify-center h-[60vh] ${className}`}>
      {box}
    </div>
  );
};

export default CulsightLoader;